import {
  Box,
  FieldSelect,
  FieldText,
  FlexBox,
  ModalBody,
  ModalFoot,
  ModalHead,
  Spinner,
} from '@dixa/lib-shared-habla';
import { boundMethod } from 'autobind-decorator';
import differenceWith from 'lodash/differenceWith';
import isEqual from 'lodash/isEqual';
import moment from 'moment-timezone';
import { Component } from 'react';
import { Field, FieldArray, Form, InjectedFormProps } from 'redux-form';

import { getConfirmButtonVerb } from 'components/powerFlowShared/helpers/getConfirmButtonVerb';
import { getRequestID } from 'components/shared/util/request';
import { FieldRepeater, RepeaterDivider } from 'fable/fieldRepeater';
import type { FlowManagerEditModalComponentT } from 'fable/flowEditor';
import { trackFlowActionSaved } from 'utils/flowEditor/tracking';

import { DispatchProps, StateProps } from '../../containers/modals/filters';
import type { ValidationT } from '../../containers/modals/validators/filters';
import {
  SystemVariableId,
  SystemVariableType,
} from '../../helpers/flowVariableHelpers';
import type {
  DateObject,
  DateRangeObject,
  FiltersFormRule,
  FiltersNodeRule,
  FiltersNodeRuleMatch,
  FiltersNodeRuleVariable,
  FlowNodeFilters,
} from '../nodeTypes/filters';
import { FlowNodeTypes, MatchingOperator, MatchingStrategy } from '../types';

import { CriteriaFields, MatchIdType } from './filters/criteriaFields';
import styles from './filters.module.scss';

export type FormValues = {
  matching_strategy: MatchingStrategy;
  rules: FiltersFormRule[];
  description: string;
};

enum FormStatus {
  Initial = 'init',
  Mounting = 'mounting',
  Mounted = 'mounted',
}

type State = {
  hasError: boolean;
  formStatus: FormStatus;
};

export type Props = FlowManagerEditModalComponentT<FlowNodeFilters> &
  InjectedFormProps<FormValues, Props, ValidationT> &
  StateProps &
  DispatchProps;

export class Filters extends Component<Props, State> {
  static getDerivedStateFromError() {
    // Update state so the next render will show the fallback UI.
    return { hasError: true };
  }

  state = {
    formStatus: FormStatus.Initial,
    hasError: false,
  };

  componentDidMount() {
    setTimeout(() => {
      this.setState({
        formStatus: FormStatus.Mounting,
      });
    });
  }

  @boundMethod
  onFormMounted(): void {
    this.setState({
      formStatus: FormStatus.Mounted,
    });
  }

  componentDidCatch(error, errorInfo) {
    // You can also log the error to an error reporting service
    console.log(error, errorInfo);
  }

  @boundMethod
  handleTriggerEvent(
    rules: FormValues['rules'],
    initialRules: FormValues['rules'],
  ): void {
    const { flowType, node } = this.props;

    const addedRules = differenceWith(rules, initialRules, isEqual).map(
      ({ variable }) => this.getVariableObj(variable).id,
    );
    const removedRules =
      node.nodeType === FlowNodeTypes.FILTERS
        ? differenceWith(initialRules, rules, isEqual).map(
            ({ variable }) => this.getVariableObj(variable).id,
          )
        : [];

    trackFlowActionSaved(!!node.isAction, {
      flowType: flowType,
      nodeType: FlowNodeTypes.FILTERS,
      settings: {
        ...(addedRules.length && { added: addedRules }),
        ...(removedRules.length && { removed: removedRules }),
      },
    });
  }

  @boundMethod
  saveClickHandler(values: FormValues): void {
    const {
      initialValues: { rules: initialRules },
      replaceNode,
      triggerClose,
    } = this.props;

    values.rules = values.rules.map((r) => this._prepareRule(r));

    this.handleTriggerEvent(values.rules, initialRules);

    replaceNode(this._prepareNode(values));
    triggerClose();
  }

  @boundMethod
  getLocalDateString(date: string, timezone: string): null | string {
    const localDateObject = moment.utc(date, moment.ISO_8601).tz(timezone);
    const isValidISOString = moment(
      localDateObject.toISOString(),
      moment.ISO_8601,
    ).isValid();

    return isValidISOString ? localDateObject.format() : null;
  }

  getVariableObj(
    value: string | DateObject | DateRangeObject | number | string[] = '',
    variableType = '',
  ): FiltersNodeRuleMatch | FiltersNodeRuleVariable {
    // type guard for DateObject
    function isValidDateObject(
      dateObject: DateObject,
    ): dateObject is DateObject {
      return (dateObject as DateObject).date !== undefined;
    }

    // type guard for DateRangeObject
    function isValidDateRangeObject(
      dateRangeObject: DateRangeObject,
    ): dateRangeObject is DateRangeObject {
      return (
        (dateRangeObject as DateRangeObject).startDate !== undefined &&
        (dateRangeObject as DateRangeObject).endDate !== undefined
      );
    }

    if (typeof value === 'string') {
      const variableRegex = /((scope|system):)?([^}]+)/;
      const match = variableRegex.exec(value);

      if (match !== null && match[2] && match[3]) {
        return { type: match[2], id: match[3] };
      }
    }

    if (
      typeof value === 'object' &&
      !Array.isArray(value) &&
      variableType === MatchIdType.Date &&
      isValidDateObject(value) &&
      value.timezone
    ) {
      const localDateISOString = this.getLocalDateString(
        value.date as string,
        value.timezone as string,
      );

      if (localDateISOString) {
        return {
          type: SystemVariableType.Input,
          id: SystemVariableId.Date,
          value: `${localDateISOString}[${value.timezone}]`,
        };
      }
    }

    if (variableType === MatchIdType.Values && typeof value === 'string') {
      // for now only auth_level uses values
      return {
        type: SystemVariableType.Input,
        id: SystemVariableId.AuthLevel,
        value,
      };
    }
    if (
      typeof value === 'object' &&
      !Array.isArray(value) &&
      variableType === MatchIdType.DateRange &&
      isValidDateRangeObject(value) &&
      value.timezone
    ) {
      const localStartDateISOString = this.getLocalDateString(
        value.startDate as string,
        value.timezone as string,
      );
      const localEndDateISOString = this.getLocalDateString(
        value.endDate as string,
        value.timezone as string,
      );

      return {
        type: SystemVariableType.Input,
        id: SystemVariableId.DateRange,
        value: {
          start_date: `${localStartDateISOString}[${value.timezone}]`,
          end_date: `${localEndDateISOString}[${value.timezone}]`,
        },
      };
    }

    if (
      typeof value === 'number' &&
      variableType === MatchIdType.SpamProbabilityVariable
    ) {
      return {
        type: SystemVariableType.Input,
        id: SystemVariableId.SpamProbability,
        value: value * 0.01, // Convert from the range input values (1-100) to the 0 to 1 value the service expects.
      };
    }

    if (variableType === MatchIdType.Input && Array.isArray(value)) {
      return {
        type: SystemVariableType.Input,
        id: SystemVariableId.Set,
        value,
      };
    }

    return {
      type: SystemVariableType.Input,
      value: typeof value === 'string' ? value : '',
      id: '',
    };
  }

  @boundMethod
  _prepareNode(values: FormValues): FlowNodeFilters {
    const { node } = this.props;
    const { payload } = node;

    const matching_strategy: MatchingStrategy =
      values.matching_strategy || MatchingStrategy.ANY;
    const description = values.description ?? '';
    const rules: FiltersNodeRule[] = values.rules.map((rule) => {
      return {
        matching_rule: rule.matching_rule || MatchingOperator.EQUALS,
        variable: this.getVariableObj(rule.variable),
        match: this.getVariableObj(rule.match, rule.variableType),
      };
    });

    return {
      ...node,
      badges: [],
      icon: 'i-bug',
      isAction: false,
      isEditable: true,
      isRemovable: true,
      nodeType: FlowNodeTypes.FILTERS,
      overlay: [],
      payload: {
        matching_strategy,
        rules,
        match_node_id: payload?.match_node_id || getRequestID(),
        non_match_node_id: payload?.non_match_node_id || getRequestID(),
        description,
      },
      text: 'Branch',
    };
  }

  /**
   * Converts client rule representation to backend expected format. For
   * example:
   *
   * {variable: "contact_custom_attribute:", contact_custom_attribute: "<uuid>"}
   * -> {variable: "contact_custom_attribute:<uuid>"}
   */
  _prepareRule(rule: FiltersFormRule): FiltersFormRule {
    if (rule.custom_attribute_id || rule.match_custom_attribute_id) {
      const {
        custom_attribute_id,
        match_custom_attribute_id,
        ...otherRuleProps
      } = rule;

      otherRuleProps.variable = custom_attribute_id
        ? `${rule.variable}${custom_attribute_id}`
        : otherRuleProps.variable;
      otherRuleProps.match = match_custom_attribute_id
        ? `${rule.match}${match_custom_attribute_id}`
        : otherRuleProps.match;
      return otherRuleProps;
    }

    return rule;
  }

  render() {
    const {
      changeFormField,
      flowType,
      handleSubmit,
      matching_strategy,
      node,
      nodeLabel,
      pristine,
      rules,
      scopeVars,
      triggerClose,
    } = this.props;
    const { formStatus } = this.state;
    const [lastRule] = [...rules].reverse();
    const isLastRuleSpamProbability =
      this.getVariableObj(lastRule.variable).id === 'spam_probability';

    const defaultVariableType = isLastRuleSpamProbability
      ? MatchIdType.SpamProbabilityVariable
      : MatchIdType.Input;

    return (
      <Box width="600px">
        <ModalHead title={nodeLabel} onClose={triggerClose}>
          <div className={styles.description}>
            Sort conversations based on whether they match your chosen criteria.
          </div>
        </ModalHead>
        {formStatus !== FormStatus.Mounted && (
          <FlexBox justifyContent="center" alignItems="center">
            <Spinner />
          </FlexBox>
        )}
        {formStatus !== FormStatus.Initial && (
          <Form onSubmit={handleSubmit(this.saveClickHandler)}>
            <ModalBody>
              <Field
                label="Add a description"
                name="description"
                component={FieldText}
                placeholder="The purpose of this filter"
                required
              />
              <Field
                component={FieldSelect}
                name="matching_strategy"
                label="Filter conversation if:"
                items={[
                  {
                    id: MatchingStrategy.ANY,
                    name: 'One or more of the criteria are met',
                  },
                  {
                    id: MatchingStrategy.ALL,
                    name: 'All of the criteria are met',
                  },
                ]}
              />
              <FieldArray
                buttonProps={{ placement: 'outside', label: 'New criteria' }}
                className={styles.fieldRepeaterWrapper}
                component={FieldRepeater}
                defaultRowProps={{
                  ...lastRule,
                  match: isLastRuleSpamProbability ? 50 : '',
                  variableType: defaultVariableType,
                }}
                divider={
                  <RepeaterDivider
                    label={
                      matching_strategy === MatchingStrategy.ALL ? 'AND' : 'OR'
                    }
                  />
                }
                name="rules"
                onMount={this.onFormMounted}
                rowComponent={CriteriaFields}
                rowProps={{
                  flowType,
                  rules,
                  scopeVariables: scopeVars,
                  changeFormField,
                }}
                title="Criteria"
              />
            </ModalBody>
            <ModalFoot
              cancelOnClick={triggerClose}
              confirmName={getConfirmButtonVerb(node)}
              confirmOnClick={handleSubmit(this.saveClickHandler)}
              confirmProps={{ disabled: pristine }}
            />
          </Form>
        )}
      </Box>
    );
  }
}
